/**
 * ✅ Created for authStore_v1 migration - 2026-01-07
 * 
 * Organization Hook - Simple Wrapper
 * 
 * Provides organization data from authStore_v1.
 * Returns raw data - no mapping, no transformation.
 * 
 * @example
 * const org = useOrg();
 * console.log(org.selectedOrg?.orgName);
 * org.selectOrganization(orgId);
 */

import { useAuthStoreV1, type Organization } from '@/stores/authStore_v1';
import { useShallow } from 'zustand/react/shallow';

/**
 * Hook to access organization state and actions
 * 
 * Returns RAW data from authStore_v1 - no mapping!
 * 
 * @example
 * const org = useOrg();
 * console.log(org.organizations);           // All orgs
 * console.log(org.selectedOrg?.orgName);    // Selected org (full object!)
 * console.log(org.selectedOrg?.latestMetadataRetrieval?.id);  // Retrieval ID
 * org.selectOrganization(orgId);            // Switch org
 */
export function useOrg() {
  // Get raw data from store (no mapping, no transformation)
  const { organizations, selectedOrg, selectOrganization } = useAuthStoreV1(
    useShallow((s) => ({
      organizations: s.organizations,
      selectedOrg: s.selectedOrg,
      selectOrganization: s.selectOrganization,
    }))
  );
  
  // Computed values
  const hasOrganizations = organizations.length > 0;
  const organizationCount = organizations.length;
  const selectedOrgId = selectedOrg?.id || null;
  
  return {
    // Raw data from store
    organizations,
    selectedOrg,        // Full object with all metadata!
    selectedOrgId,      // Just the ID (for convenience)
    
    // Computed
    hasOrganizations,
    organizationCount,
    
    // Actions
    selectOrganization,
    
    // Helper to find org by ID
    getOrganizationById: (id: string) => 
      organizations.find(org => org.id === id) || null,
  };
}

/**
 * Hook to get organization by ID
 * 
 * @param id - Organization ID
 * @returns Organization or null
 */
export function useOrganizationById(id: string | null): Organization | null {
  return useAuthStoreV1((state) => 
    id ? state.organizations.find(org => org.id === id) || null : null
  );
}

/**
 * Hook to get selected organization (convenience)
 */
export function useSelectedOrganization(): Organization | null {
  return useAuthStoreV1((state) => state.selectedOrg);
}

export default useOrg;

